import type { SearchFilterOption } from "@/components/Search/types"

export const CommonStoreActions = {
  setItem<State>(this: State, field: keyof State, value: any) {
    this[field] = value
  },
  reset<State extends { [key: string]: any }>(this: State, initialState: State) {
    for (const [key, value] of Object.entries(initialState)) {
      if (Array.isArray(value)) {
        this[key as keyof State] = [] as State[keyof State]
      } else {
        this[key as keyof State] = value
      }
    }
  },
  /**
   * Dynamically creates and stores a new AbortController under abortController.{name}.
   * If one already exists, it will be aborted before replacement.
   * Example: store.addAbortController('compareApi')
   */
  addApiAbortController<State extends { [key: string]: any }>(this: State, name: string): AbortController {
    if (!this['abortController']) {
      (this as any)['abortController'] = {}
    }

    // Abort existing controller if it exists
    const existing = this['abortController'][name]
    if (existing) {
      existing.abort()
    }

    // Create and store new one
    const controller = new AbortController()
    this['abortController'][name] = controller

    return controller
  },


  /**
   * Aborts one or multiple AbortControllers by name(s).
   * Example:
   *  store.abortApiCall('compareApi')
   *  store.abortApiCall(['compareApi', 'checkDiffStatusApi'])
   */
  abortApiCall<State extends { [key: string]: any }>(this: State, names: string | string[]) {
    const abortControllers = this['abortController']
    if (!abortControllers) return

    const list = Array.isArray(names) ? names : [names]

    for (const name of list) {
      const controller = abortControllers[name]
      if (controller) {
        (controller as AbortController).abort()
        delete abortControllers[name]
      }
    }
  },

  /**
   * Aborts all AbortControllers registered in state.abortController
   * Example: store.abortAllApiCalls()
   */
  abortAllApiCalls<State extends { [key: string]: any }>(this: State) {
    const abortControllers = this['abortController']
    if (abortControllers) {
      for (const [key, controller] of Object.entries(abortControllers)) {
        (controller as AbortController).abort()
      }
      (this as any)['abortController'] = {} // reset
    }
  },
}

export const CommonStoreGetters = {
  getItem:
    <State>(state: State) =>
    (field: keyof State) => {
      const value = state[field]
      if (typeof value === 'boolean') {
        return value
      } else if (Array.isArray(value)) {
        return value.length > 0 ? value : []
      } else if (typeof value === 'string') {
        return value.length > 0 ? value : ''
      } else {
        return value !== undefined ? value : ''
      }
    },
  /**
   * Get a specific AbortController by name
   * Example: store.getAbortController('compareApi')
   */
  getAbortController:
    <State extends { [key: string]: any }>(state: State) =>
    (name: string): AbortController | undefined => {
      return state['abortController']?.[name]
    }
}

function extractCount(str: string): string {
  const match = str.match(/\(\d+\)/);
  return match ? match[0] : "";
}

export const getFullICSCodeText = (findICSCodes: SearchFilterOption[], icsCodeLookUpOptions: SearchFilterOption[])  => {
  const updatedICSCodes =findICSCodes.map(n => {
    const count = extractCount(n.text);   // e.g., "(35592)"
    const match = icsCodeLookUpOptions.find(h => h.id === n.id);
    return {
      ...n,
      text: match ? `${match.text} ${count}` : n.text
    };
  });
  return updatedICSCodes;
}